import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { Divider, Text } from '@wix/wix-ui-tpa/cssVars';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';

import { DataHook, Experiment } from '../../../constants';
import { IMAGE_ROUTE } from '../../../constants/image-route';
import { BirthdaySettings, SocialMediaChannel, SocialMediaSettings } from '../../../types/domain';
import { isBuilderEditor } from '../../../utils';
import settingsParams from '../settingsParams';
import { StepThumbnail } from '../stylesParams';
import { AddBirthdayButton } from './AddBirthdayButton';
import { classes } from './DetailsCard.st.css';
import { useControllerProps } from './hooks';
import { SocialMediaRule } from './SocialMediaRule';

type DetailsItem = {
  title?: string;
  description: string;
  socialMedia?: SocialMediaSettings;
  birthday?: BirthdaySettings;
};

interface DetailsCardProps {
  step: number;
  title: string;
  details: DetailsItem[];
  iconPath: string;
  dataHook?: string;
  showTiers: boolean;
  description?: string;
  followedChannels: SocialMediaChannel[];
  onFollowSocialMediaChannel?(type: SocialMediaChannel): void;
}

export const DetailsCard: React.FC<DetailsCardProps> = ({
  step,
  title,
  details,
  iconPath,
  dataHook,
  showTiers,
  description,
  followedChannels,
  onFollowSocialMediaChannel,
}) => {
  const styles = useStyles();
  const settings = useSettings();
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const { pointsMultiplier } = useControllerProps();

  const showPointsMultiplier =
    dataHook === DataHook.DetailsCardEarnPoints &&
    experiments.enabled(Experiment.EnablePointsAdjustment) &&
    !isBuilderEditor() &&
    !!pointsMultiplier?.name;

  const showStepIndication = styles
    .getStylesForAllBreakpoints()
    .some((breakpointStyles) => breakpointStyles.booleans.showStepIndication);
  const showStepIcon = styles
    .getStylesForAllBreakpoints()
    .some((breakpointStyles) => breakpointStyles.numbers.cardsStepThumbnail === StepThumbnail.Icon);
  const showStepNumber = styles
    .getStylesForAllBreakpoints()
    .some((breakpointStyles) => breakpointStyles.numbers.cardsStepThumbnail === StepThumbnail.Numbers);

  const stepIndicationTag = settings.get(settingsParams.detailsStepIndicationTag);
  const stepTag = settings.get(settingsParams.detailsStepTag);
  const titleTag = settings.get(settingsParams.detailsTitleTag);
  const descriptionTag = settings.get(settingsParams.detailsDescriptionTag);

  const renderPointsMultiplierContent = () => {
    if (!pointsMultiplier?.name) {
      return null;
    }

    return (
      <>
        <Divider className={classes.pointsMultiplierDivider} />
        <Text tagName={titleTag} className={classes.multiplierDetailTitle}>
          {pointsMultiplier.name}
        </Text>
        {pointsMultiplier.description && (
          <Text tagName={descriptionTag} className={classes.detailDescription}>
            {pointsMultiplier.description}
          </Text>
        )}
      </>
    );
  };

  const renderDetails = () => {
    if (showTiers) {
      return (
        <div className={classes.details}>
          <Text tagName={descriptionTag} className={classes.detailDescription}>
            {description}
          </Text>
          {showPointsMultiplier && (
            <div className={classes.pointsMultiplierTiers} data-hook={`${dataHook}-points-multiplier`}>
              {renderPointsMultiplierContent()}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={classes.detailsContainer}>
        <ul className={classes.details}>
          {details.map((detail, index) => {
            return (
              <li key={index} aria-label={t('app.role')} className={classes.detail}>
                {detail.title ? (
                  <Text tagName={titleTag} className={classes.detailTitle}>
                    {detail.title}
                  </Text>
                ) : null}
                <Text
                  tagName={descriptionTag}
                  className={classes.detailDescription}
                  data-hook={`${dataHook}-description-${index}`}
                >
                  {detail.description}
                </Text>
                {detail.socialMedia && onFollowSocialMediaChannel && (
                  <SocialMediaRule
                    followedChannels={followedChannels}
                    isDetailsCard
                    socialMedia={detail.socialMedia}
                    onFollowSocialMediaChannel={onFollowSocialMediaChannel}
                  />
                )}
                {detail.birthday && <AddBirthdayButton description={detail.birthday.description} isDetailsCard />}
              </li>
            );
          })}
          {showPointsMultiplier && (
            <li className={classes.pointsMultiplier} data-hook={`${dataHook}-points-multiplier`}>
              {renderPointsMultiplierContent()}
            </li>
          )}
        </ul>
      </div>
    );
  };

  return (
    <li data-hook={dataHook} className={classes.root}>
      <div className={classes.header}>
        {showStepIndication && (
          <div className={classes.stepIndication}>
            {showStepIcon && (
              <img
                className={classes.icon}
                aria-hidden
                alt={t('app.image.program-step-icon')}
                src={IMAGE_ROUTE(iconPath)}
              />
            )}
            {showStepNumber && <Text tagName={stepIndicationTag} className={classes.step}>{`0${step}`}</Text>}
          </div>
        )}
        <Text tagName={stepTag} className={classes.headerTitle} data-hook={`${dataHook}-title`}>
          {title}
        </Text>
      </div>
      {renderDetails()}
    </li>
  );
};
